import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import harnessImage from "../assets/images/harness.png";

/* ── Address formatter ── */
const UPPERCASE_TOKENS = new Set([
  "N","S","E","W","NE","NW","SE","SW","NNE","NNW","SSE","SSW","ENE","ESE","WNW","WSW",
  "ST","AVE","BLVD","DR","RD","CT","CIR","LN","PL","TER","WAY","HWY","PKWY","FWY",
  "SQ","LOOP","XING","EXT","APT","STE","UNIT","FL","BLDG","RM","LOT","PO","BOX",
]);
const ALIASES = {
  "STREET":"ST","AVENUE":"AVE","BOULEVARD":"BLVD","DRIVE":"DR","ROAD":"RD",
  "COURT":"CT","CIRCLE":"CIR","LANE":"LN","PLACE":"PL","TERRACE":"TER",
  "HIGHWAY":"HWY","PARKWAY":"PKWY","FREEWAY":"FWY","TRAIL":"TRL","CROSSING":"XING",
  "SUITE":"STE","APARTMENT":"APT","FLOOR":"FL","BUILDING":"BLDG",
  "NORTH":"N","SOUTH":"S","EAST":"E","WEST":"W",
  "NORTHEAST":"NE","NORTHWEST":"NW","SOUTHEAST":"SE","SOUTHWEST":"SW",
};

const formatAddress = (raw) => {
  if (!raw) return raw;
  return raw
    .replace(/(\d+)(st|nd|rd|th)/gi, "$1$2")
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/(\d)([a-zA-Z])/g, "$1 $2")
    .split(/\s+/)
    .map((word) => {
      if (!word) return "";
      const upper = word.toUpperCase();
      if (ALIASES[upper]) return ALIASES[upper];
      if (UPPERCASE_TOKENS.has(upper)) return upper;
      if (/^\d+$/.test(word)) return word;
      if (/^\d+(st|nd|rd|th)$/i.test(word)) return word.replace(/[a-z]+$/i, m => m.toUpperCase());
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ")
    .trim();
};

const cardStyle = {
  background: "linear-gradient(180deg,#ffffff 0%,#f4f6ff 100%)",
  border: "2px solid #ffffff",
  borderRadius: 20,
  boxShadow: "0 0 30px rgba(120,170,255,0.18), 0 14px 40px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1)",
  padding: "20px 18px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui",
};

export default function InvoiceScreenMobile() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetch(`https://eleven11-i6v4.onrender.com/api/orders/${id}`)
      .then(r => r.json())
      .then(data => setOrder(data))
      .catch(err => console.error("Failed to load order", err));
  }, [id]);

  if (!order) {
    return (
      <div style={{ padding: 24, fontFamily: "system-ui", color: "#6E6E73", textAlign: "center", paddingTop: 60 }}>
        Loading Invoice...
      </div>
    );
  }

  const customer = order?.customer || {};
  const shipping = order?.shippingAddress || {};
  const items = order?.items || [];
  const subtotal = Number(order?.subtotal || 0);
  const tax = Number(order?.tax || 0);
  const shippingCost = Number(order?.shipping || 0);
  const total = Number(order?.total || 0);
  const appliedCredit = Number(order?.appliedCredit || 0);

  const formattedDate = order?.createdAt
    ? new Date(order.createdAt).toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric"
      })
    : "—";

  const estimatedDelivery = order?.createdAt
    ? new Date(new Date(order.createdAt).getTime() + 4 * 24 * 60 * 60 * 1000)
        .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    : "—";

  return (
    <div style={{ background: "#F4F6FB", minHeight: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, system-ui", paddingBottom: 40 }}>
      <div style={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ── Invoice Header ── */}
        <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{
            fontSize: 32, width: 58, height: 58, borderRadius: 16, flexShrink: 0,
            background: "linear-gradient(180deg,#ffffff 0%,#f4f6ff 100%)",
            border: "2px solid #fff",
            boxShadow: "0 8px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            📄
          </div>
          <div>
            <div style={{ fontSize: 26, fontWeight: 600, color: "#1D1D1F" }}>Invoice</div>
            <div style={{ fontSize: 13, color: "#6E6E73", marginTop: 2 }}>Order #{order?.orderNumber || "—"}</div>
            <div style={{ fontSize: 13, color: "#6E6E73", marginTop: 2 }}>Order Date: {formattedDate}</div>
            <div style={{ marginTop: 10 }}>
              <span style={{
                display: "inline-flex", alignItems: "center", padding: "6px 12px",
                borderRadius: 999, background: "#EEF4FF", color: "#2D6CFF",
                fontSize: 12, fontWeight: 600, textTransform: "capitalize"
              }}>
                {order?.status || "pending"}
              </span>
            </div>
          </div>
        </div>

        {/* ── Billing Info ── */}
        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1D1D1F", marginBottom: 14 }}>Billing Info</div>
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{customer.firstName} {customer.lastName}</div>
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{shipping.city}, {shipping.state} {shipping.zip}</div>
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{customer.email}</div>
          {customer.phone && (
            <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{customer.phone}</div>
          )}
          <div style={{ height: 1, background: "rgba(0,0,0,0.05)", margin: "14px 0" }} />
          <div style={{ fontSize: 13, color: "#6E6E73" }}>Estimated Delivery:</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "#2D6CFF", marginTop: 3 }}>
            Arriving around {estimatedDelivery}
          </div>
        </div>

        {/* ── Shipping Address ── */}
        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1D1D1F", marginBottom: 14 }}>Shipping Address</div>
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{customer.firstName} {customer.lastName}</div>
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{formatAddress(shipping.address)}</div>
          {shipping.apartment && (
            <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{formatAddress(shipping.apartment)}</div>
          )}
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 4 }}>{shipping.city}, {shipping.state} {shipping.zip}</div>
          <div style={{ height: 1, background: "rgba(0,0,0,0.06)", margin: "14px 0" }} />
          <div style={{ fontSize: 14, color: "#1D1D1F", marginBottom: 6 }}>
            Estimated Delivery: <b>{estimatedDelivery}</b>
          </div>
          <div style={{ fontSize: 14, color: "#1D1D1F" }}>
            Shipping Method: <span style={{ color: "#2D6CFF", fontWeight: 500 }}>FREE Delivery</span>
          </div>
        </div>

        {/* ── Items Ordered ── */}
        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1D1D1F", marginBottom: 16 }}>Items Ordered</div>

          {items.length === 0 ? (
            <div style={{ fontSize: 14, color: "#8E8E93", padding: "12px 0" }}>No items found on this order.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {items.map((item, index) => {
                const numericPrice = Number(item.price || 0);
                const qty = Number(item.qty || 1);
                const lineTotal = numericPrice * qty;
                return (
                  <div key={item._id || index} style={{
                    display: "grid", gridTemplateColumns: "52px 1fr auto",
                    gap: 12, alignItems: "center",
                    padding: "14px 0",
                    borderTop: index === 0 ? "none" : "1px solid rgba(0,0,0,0.06)"
                  }}>
                    <img
                      src={
                        item.image
                          ? item.image.startsWith("http") ? item.image : item.image.replace(/^\/+/, "")
                          : harnessImage
                      }
                      alt={item.title || "Part"}
                      style={{ width: 52, height: 52, objectFit: "contain", borderRadius: 10, background: "#fff", padding: 4, boxShadow: "0 4px 10px rgba(0,0,0,0.06)" }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#1D1D1F", lineHeight: "18px" }}>
                        {item.title || "Unknown Part"}
                      </div>
                      <div style={{ fontSize: 12, color: "#8E8E93", marginTop: 2 }}>x{qty}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#1D1D1F" }}>${lineTotal.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Totals */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#1D1D1F", marginBottom: 8 }}>
              <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#1D1D1F", marginBottom: 8 }}>
              <span>Shipping</span>
              <span style={{ color: shippingCost === 0 ? "#2D6CFF" : "#1D1D1F", fontWeight: shippingCost === 0 ? 600 : 400 }}>
                {shippingCost === 0 ? "Free" : `$${shippingCost.toFixed(2)}`}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#1D1D1F", marginBottom: 8 }}>
              <span>Estimated Tax</span><span>${tax.toFixed(2)}</span>
            </div>
            {appliedCredit > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#10b981", fontWeight: 600, marginBottom: 8 }}>
                <span>Store Credit Applied</span><span>−${appliedCredit.toFixed(2)}</span>
              </div>
            )}
            <div style={{ height: 1, background: "linear-gradient(to right, transparent, #e3e3e8, transparent)", margin: "14px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 20, fontWeight: 600, color: "#1D1D1F" }}>
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

    {/* View Tracking Button */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <button
            onClick={() => window.location.href = `/account/orders/${order._id}`}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: 16,
              border: "none",
              background: "#0f0f17",
              color: "#fff",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 10px 24px rgba(0,0,0,0.12)"
            }}
          >
            View Order Tracking →
          </button>
        </div>

      </div>
    </div>
  );
}